import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { FormField } from '../shared/FormField'
import { Button } from '../shared/Button'
import PasswordStrengthIndicator from './PasswordStrengthIndicator'
import { supabase } from '../../lib/supabase'

/**
 * ChangePasswordForm Component
 *
 * Secure password change form with strength validation and requirements checking.
 *
 * Features:
 * - Current password verification
 * - New password strength indicator
 * - Password confirmation matching
 * - Real-time validation feedback
 * - Password visibility toggle
 * - Requirements checklist
 * - Success/error messaging
 */
function ChangePasswordForm() {
  const { t } = useTranslation('profile')
  const { updatePassword, user } = useAuth()

  // Form state
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // UI state
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  // Password requirements
  const passwordRequirements = useMemo(() => [
    {
      id: 'length',
      label: t('profile:security.password.requirements.length'),
      test: (password) => password.length >= 8
    },
    {
      id: 'uppercase',
      label: t('profile:security.password.requirements.uppercase'),
      test: (password) => /[A-Z]/.test(password)
    },
    {
      id: 'lowercase',
      label: t('profile:security.password.requirements.lowercase'),
      test: (password) => /[a-z]/.test(password)
    },
    {
      id: 'number',
      label: t('profile:security.password.requirements.number'),
      test: (password) => /\d/.test(password)
    },
    {
      id: 'special',
      label: t('profile:security.password.requirements.special'),
      test: (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password)
    }
  ], [t])

  // Calculate password strength
  const passwordStrength = useMemo(() => {
    const password = formData.newPassword
    if (!password) return 0

    let strength = 0
    passwordRequirements.forEach(req => {
      if (req.test(password)) strength++
    })

    return strength
  }, [formData.newPassword, passwordRequirements])

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }

    // Clear submit error when user modifies form
    if (submitError) {
      setSubmitError(null)
    }

    // Check confirm password match in real-time
    if (name === 'newPassword' && formData.confirmPassword) {
      if (value !== formData.confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: t('profile:security.password.errors.mismatch') }))
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: null }))
      }
    }

    if (name === 'confirmPassword' && formData.newPassword) {
      if (value !== formData.newPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: t('profile:security.password.errors.mismatch') }))
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: null }))
      }
    }
  }, [errors, submitError, formData, t])

  // Toggle password visibility
  const togglePasswordVisibility = useCallback((field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
  }, [])

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {}

    // Validate current password
    if (!formData.currentPassword) {
      newErrors.currentPassword = t('profile:security.password.errors.currentRequired')
    }

    // Validate new password
    if (!formData.newPassword) {
      newErrors.newPassword = t('profile:security.password.errors.newRequired')
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('profile:security.password.errors.tooShort')
    } else if (passwordStrength < 3) {
      newErrors.newPassword = t('profile:security.password.errors.tooWeak')
    } else if (formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = t('profile:security.password.errors.samePassword')
    }

    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('profile:security.password.errors.confirmRequired')
    } else if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = t('profile:security.password.errors.mismatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, passwordStrength, t])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setShowSuccess(false)

    try {
      // First, verify the current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email,
        password: formData.currentPassword
      })

      if (signInError) {
        setErrors({ currentPassword: t('profile:security.password.errors.invalidCurrent') })
        setIsSubmitting(false)
        return
      }

      // Update the password
      await updatePassword(formData.newPassword)

      // Success
      setShowSuccess(true)
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' })

      // Hide success message after 10 seconds
      setTimeout(() => {
        setShowSuccess(false)
      }, 10000)

    } catch (error) {
      console.error('Password update error:', error)
      setSubmitError(error.message || t('profile:security.password.errors.updateFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // Password visibility toggle button
  const PasswordToggle = ({ field, show }) => (
    <button
      type="button"
      onClick={() => togglePasswordVisibility(field)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors"
      aria-label={show ? t('profile:security.hidePassword') : t('profile:security.showPassword')}
    >
      {show ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
    </button>
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{t('profile:security.password.success.title')}</p>
              <p className="text-sm mt-1">{t('profile:security.password.success.message')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{t('profile:security.password.error.title')}</p>
              <p className="text-sm mt-1">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Current Password */}
      <FormField
        label={t('profile:security.password.current.label')}
        error={errors.currentPassword}
        helperText={!errors.currentPassword && t('profile:security.password.current.helper')}
        required
      >
        <div className="relative">
          <input
            type={showPasswords.current ? 'text' : 'password'}
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            placeholder={t('profile:security.password.current.placeholder')}
            disabled={isSubmitting}
            autoComplete="current-password"
            className={`w-full px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors
              ${errors.currentPassword
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 hover:border-gray-400'
              }
              ${isSubmitting ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
            aria-invalid={!!errors.currentPassword}
          />
          <PasswordToggle field="current" show={showPasswords.current} />
        </div>
      </FormField>

      <div className="border-t border-gray-200 pt-6">
        {/* New Password */}
        <FormField
          label={t('profile:security.password.new.label')}
          error={errors.newPassword}
          required
        >
          <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder={t('profile:security.password.new.placeholder')}
              disabled={isSubmitting}
              autoComplete="new-password"
              className={`w-full px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors
                ${errors.newPassword
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 hover:border-gray-400'
                }
                ${isSubmitting ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              aria-invalid={!!errors.newPassword}
              aria-describedby="password-strength"
            />
            <PasswordToggle field="new" show={showPasswords.new} />
          </div>

          {/* Password Strength Indicator */}
          {formData.newPassword && (
            <div className="mt-3" id="password-strength">
              <PasswordStrengthIndicator
                password={formData.newPassword}
                requirements={passwordRequirements}
                strength={passwordStrength}
              />
            </div>
          )}
        </FormField>

        {/* Confirm Password */}
        <FormField
          label={t('profile:security.password.confirm.label')}
          error={errors.confirmPassword}
          helperText={!errors.confirmPassword && formData.confirmPassword && formData.newPassword === formData.confirmPassword
            ? t('profile:security.password.confirm.match')
            : null
          }
          required
        >
          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('profile:security.password.confirm.placeholder')}
              disabled={isSubmitting || !formData.newPassword}
              autoComplete="new-password"
              className={`w-full px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors
                ${errors.confirmPassword
                  ? 'border-red-300 bg-red-50'
                  : formData.confirmPassword && formData.newPassword === formData.confirmPassword
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }
                ${isSubmitting || !formData.newPassword ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              aria-invalid={!!errors.confirmPassword}
            />
            <PasswordToggle field="confirm" show={showPasswords.confirm} />

            {/* Match indicator */}
            {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
        </FormField>
      </div>

      {/* Security Tips */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">{t('profile:security.tips.title')}</p>
            <ul className="space-y-1 text-amber-700">
              <li>• {t('profile:security.tips.unique')}</li>
              <li>• {t('profile:security.tips.manager')}</li>
              <li>• {t('profile:security.tips.twoFactor')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4 border-t border-gray-200">
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={isSubmitting}
          disabled={isSubmitting || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword || passwordStrength < 3}
        >
          {isSubmitting ? t('profile:security.password.button.updating') : t('profile:security.password.button.update')}
        </Button>
      </div>
    </form>
  )
}

export default ChangePasswordForm